// 117 — Certificado Médico (SNS-MSP/HCU-form.117/2021, PDF oficial págs. 244-247 del visor). Piezas puras del formulario, para poder probarlas:
// - Reposo: el instructivo dice "deberá marcar con una X si es necesario o no el reposo. No se deberá dejar en blanco este espacio": el "No"
//   también se registra (sección del Composition y par del PDF), no solo el "Sí".
// - Horas de reposo: número entero, "en números y letras". Un decimal ("1.5") no tiene lectura en letras: se rechaza en vez de imprimirlo sin letras.
// - Desde / hasta: "las fechas escritas están incluidas en el período de reposo", por lo que "hasta" no puede ser anterior a "desde".
// - Toda fecha "en números y letras" cae al texto ISO si no se puede convertir: nunca imprime "undefined" ni "null".
import { fechaNumerosYLetras } from '../fechaEnLetras';
import { numeroALetras } from '../recetas/numeroALetras';

/** "2026-09-03" -> "03/09/2026 (tres de septiembre de dos mil veintiséis)"; si no se puede convertir, el texto tal cual. */
export function fechaTexto117(fecha: string): string {
  return fechaNumerosYLetras(fecha) ?? fecha;
}

/** "24" -> "24 (veinticuatro)" — el instructivo pide las horas de reposo en números y en letras. */
export function horasNumerosYLetras(horas: number): string {
  return Number.isInteger(horas) && horas >= 0 ? `${horas} (${numeroALetras(horas)})` : String(horas);
}

export interface Reposo117 {
  readonly reposo: boolean;
  /** Texto tal cual lo escribió el usuario ('' si no escribió nada). */
  readonly horas: string;
  readonly desde: string;
  readonly hasta: string;
}

/** Horas como entero >= 0; `undefined` si está vacío o no es un entero. */
export function horasEnteras117(horas: string): number | undefined {
  const limpio = horas.trim();
  return /^\d+$/.test(limpio) ? Number(limpio) : undefined;
}

/** Lo que impide guardar el reposo (vacío si está bien o si no hay reposo). */
export function faltantesReposo117(r: Reposo117): string[] {
  if (!r.reposo) return [];
  const faltan: string[] = [];
  if (r.horas.trim() !== '' && horasEnteras117(r.horas) === undefined) faltan.push('Horas de reposo: escriba un número entero de horas (sin decimales ni letras).');
  if (r.desde && r.hasta && r.hasta < r.desde) faltan.push('Reposo: la fecha "hasta" no puede ser anterior a la fecha "desde".');
  return faltan;
}

/** Texto del reposo para la sección del Composition y el `Par` del PDF: "No" si no se recomienda; si se recomienda, horas y fechas en números y letras. */
export function textoReposo117(r: Reposo117): string {
  if (!r.reposo) return 'No';
  const horas = horasEnteras117(r.horas);
  return (
    [
      horas !== undefined ? `${horasNumerosYLetras(horas)} horas` : undefined,
      r.desde ? `Desde: ${fechaTexto117(r.desde)}` : undefined,
      r.hasta ? `Hasta: ${fechaTexto117(r.hasta)}` : undefined,
    ]
      .filter(Boolean)
      .join(' — ') || 'Sí'
  );
}

/** Ingreso/alta de la hospitalización, con el mismo respaldo de fecha. */
export function textoHospitalizacion117(fechaIngreso: string, fechaAlta: string): string {
  return [fechaIngreso ? `Ingreso: ${fechaTexto117(fechaIngreso)}` : undefined, fechaAlta ? `Alta: ${fechaTexto117(fechaAlta)}` : undefined].filter(Boolean).join(' — ') || 'Sí';
}
